import { API_URL } from '../config';
import { useBranding } from '../context/BrandingContext';
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Send } from 'lucide-react';
import axios from 'axios';

const CSATPesquisa = () => {
  const { token } = useParams();
  const { branding } = useBranding();

  const [searchParams] = useSearchParams();
  const notaParam = searchParams.get('nota');
  const resolvidoParam = searchParams.get('resolvido');

  const [loading, setLoading] = useState(true);
  const [pesquisa, setPesquisa] = useState(null);
  const [problemaResolvido, setProblemaResolvido] = useState(null);
  const [nota, setNota] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [etapa, setEtapa] = useState('resolvido'); // 'resolvido', 'nota', 'motivo', 'obrigado'
  const [erro, setErro] = useState(null);


  const emojis = [
    { nota: 1, emoji: '\u{1F621}', label: 'Pessimo', cor: '#e74c3c' },
    { nota: 2, emoji: '\u{1F615}', label: 'Ruim', cor: '#e67e22' },
    { nota: 3, emoji: '\u{1F610}', label: 'Regular', cor: '#f39c12' },
    { nota: 4, emoji: '\u{1F642}', label: 'Bom', cor: '#27ae60' },
    { nota: 5, emoji: '\u{1F60D}', label: 'Excelente', cor: 'var(--brand, #20a490)' },
  ];

  useEffect(() => {
    carregarPesquisa();
  }, [token]);

  useEffect(() => {
    if (pesquisa && !pesquisa.respondido) {
      if (resolvidoParam === 'sim') {
        setProblemaResolvido(true);
        setEtapa('nota');
      } else if (resolvidoParam === 'nao') {
        setProblemaResolvido(false);
        setEtapa('nota');
      }
      if (notaParam) {
        const n = parseInt(notaParam);
        if (n >= 1 && n <= 5) {
          setNota(n);
          // Se clicou direto na nota pelo email, pula para motivo
          setEtapa('motivo');
        }
      }
    }
  }, [pesquisa, notaParam, resolvidoParam]);

  const carregarPesquisa = async () => {
    try {
      const response = await axios.get(`${API_URL}/pesquisas/csat/${token}`);
      setPesquisa(response.data);
      if (response.data.respondido) {
        setNota(response.data.nota);
        setProblemaResolvido(response.data.problema_resolvido);
        setEtapa('obrigado');
      }
    } catch (error) {
      setErro(error.response?.data?.detail || 'Pesquisa nao encontrada');
    } finally {
      setLoading(false);
    }
  };

  const selecionarResolvido = (valor) => {
    setProblemaResolvido(valor);
    setEtapa('nota');
  };

  const selecionarNota = (n) => {
    setNota(n);
    setEtapa('motivo');
  };

  const enviarResposta = async () => {
    setEnviando(true);
    try {
      await axios.post(`${API_URL}/pesquisas/csat/responder`, {
        token,
        problema_resolvido: problemaResolvido,
        nota,
        motivo: motivo.trim() || null
      });
      setEtapa('obrigado');
    } catch (error) {
      setErro(error.response?.data?.detail || 'Erro ao enviar resposta');
    } finally {
      setEnviando(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.loading}>Carregando...</div>
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <AlertCircle size={48} color="#e74c3c" />
          <h2 style={{ color: '#e74c3c', marginTop: '15px' }}>Ops!</h2>
          <p style={{ color: '#666' }}>{erro}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.headerBar}>
          <span style={styles.headerText}>{branding.nome_operadora}</span>
        </div>

        {pesquisa?.protocolo && (
          <p style={styles.protocolo}>Protocolo: #{pesquisa.protocolo}</p>
        )}
        {pesquisa?.atendente_nome && (
          <p style={styles.atendente}>Atendente: {pesquisa.atendente_nome}</p>
        )}

        {/* Etapa 1: Problema resolvido? */}
        {etapa === 'resolvido' && (
          <>
            <h1 style={styles.titulo}>Seu problema foi resolvido?</h1>
            <div style={styles.botoesResolvido}>
              <button onClick={() => selecionarResolvido(true)} style={styles.btnSim}>
                Sim
              </button>
              <button onClick={() => selecionarResolvido(false)} style={styles.btnNao}>
                Nao
              </button>
            </div>
          </>
        )}

        {/* Etapa 2: Nota com emojis */}
        {etapa === 'nota' && (
          <>
            <h1 style={styles.titulo}>Como voce avalia o atendimento?</h1>
            <div style={styles.emojisContainer}>
              {emojis.map((e) => (
                <button
                  key={e.nota}
                  onClick={() => selecionarNota(e.nota)}
                  style={{
                    ...styles.emojiBtn,
                    transform: nota === e.nota ? 'scale(1.2)' : 'scale(1)',
                    opacity: nota && nota !== e.nota ? 0.5 : 1,
                  }}
                >
                  <span style={styles.emoji}>{e.emoji}</span>
                  <span style={{ color: e.cor, fontSize: '12px' }}>{e.label}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Etapa 3: Motivo */}
        {etapa === 'motivo' && (
          <>
            <div style={styles.resumo}>
              <span style={{ fontSize: '14px', color: '#666' }}>
                {problemaResolvido ? 'Problema resolvido' : 'Problema nao resolvido'} |
                Nota: {emojis.find(e => e.nota === nota)?.emoji} {emojis.find(e => e.nota === nota)?.label}
              </span>
            </div>
            <h2 style={styles.tituloMotivo}>Conte o motivo da sua avaliacao</h2>
            <p style={styles.subtitulo}>(Opcional) Sua opiniao nos ajuda a melhorar</p>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ex: O atendimento foi rapido, mas..."
              style={styles.textarea}
              rows={4}
            />
            <button
              onClick={enviarResposta}
              disabled={enviando}
              style={styles.botaoEnviar}
            >
              <Send size={18} />
              {enviando ? 'Enviando...' : 'Enviar avaliacao'}
            </button>
            <button
              onClick={() => { setMotivo(''); enviarResposta(); }}
              style={styles.botaoPular}
            >
              Pular e enviar
            </button>
          </>
        )}

        {/* Etapa 4: Obrigado */}
        {etapa === 'obrigado' && (
          <>
            <CheckCircle size={64} color="var(--brand, #20a490)" />
            <h2 style={styles.tituloObrigado}>Muito obrigado!</h2>
            <p style={styles.textoObrigado}>
              Sua avaliacao e muito importante para melhorarmos nosso atendimento.
            </p>
          </>
        )}

        {/* Rodape */}
        <div style={styles.rodape}>
          <p style={styles.rodapeTexto}>{branding.nome_operadora}{branding.tagline ? ` - ${branding.tagline}` : ''}</p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    textAlign: 'center',
  },
  headerBar: {
    backgroundColor: 'var(--brand, #20a490)',
    margin: '-40px -40px 25px -40px',
    padding: '25px',
    borderRadius: '16px 16px 0 0',
  },
  headerText: {
    color: 'white',
    fontSize: '22px',
    fontWeight: 'bold',
    letterSpacing: '2px',
  },
  protocolo: {
    fontSize: '13px',
    color: '#999',
    marginBottom: '4px',
  },
  atendente: {
    fontSize: '13px',
    color: '#999',
    marginBottom: '25px',
  },
  titulo: {
    fontSize: '22px',
    color: '#333',
    marginBottom: '25px',
    fontWeight: '600',
  },
  subtitulo: {
    fontSize: '14px',
    color: '#888',
    marginBottom: '15px',
  },
  botoesResolvido: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    marginBottom: '20px',
  },
  btnSim: {
    padding: '15px 40px',
    fontSize: '18px',
    fontWeight: 'bold',
    borderRadius: '10px',
    border: 'none',
    backgroundColor: '#27ae60',
    color: 'white',
    cursor: 'pointer',
  },
  btnNao: {
    padding: '15px 40px',
    fontSize: '18px',
    fontWeight: 'bold',
    borderRadius: '10px',
    border: 'none',
    backgroundColor: '#e74c3c',
    color: 'white',
    cursor: 'pointer',
  },
  emojisContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '25px',
    flexWrap: 'wrap',
  },
  emojiBtn: {
    background: 'none',
    border: '2px solid transparent',
    borderRadius: '12px',
    padding: '10px 8px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    transition: 'transform 0.2s',
  },
  emoji: {
    fontSize: '40px',
    lineHeight: '1',
  },
  resumo: {
    backgroundColor: '#f9f9f9',
    padding: '10px 15px',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  tituloMotivo: {
    fontSize: '20px',
    color: '#333',
    marginBottom: '8px',
  },
  textarea: {
    width: '100%',
    padding: '15px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '14px',
    resize: 'vertical',
    marginBottom: '20px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  botaoEnviar: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'var(--brand, #20a490)',
    color: 'var(--brand-ink, #fff)',
    border: 'none',
    padding: '12px 30px',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: 'pointer',
  },
  botaoPular: {
    display: 'block',
    background: 'none',
    border: 'none',
    color: '#999',
    fontSize: '14px',
    cursor: 'pointer',
    marginTop: '12px',
    margin: '12px auto 0',
  },
  tituloObrigado: {
    fontSize: '28px',
    color: 'var(--brand, #20a490)',
    marginTop: '20px',
    marginBottom: '15px',
  },
  textoObrigado: {
    fontSize: '16px',
    color: '#666',
    lineHeight: '1.5',
  },
  rodape: {
    borderTop: '1px solid #eee',
    marginTop: '30px',
    paddingTop: '20px',
  },
  rodapeTexto: {
    color: 'var(--brand, #20a490)',
    fontWeight: '600',
    fontSize: '14px',
  },
  loading: {
    padding: '40px',
    color: '#666',
  },
};

export default CSATPesquisa;
